/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import Topheader from "../../../../Layout/header/header";
import Sidebar from "../sidebar/sidebar";
import APIManager from "../../../../Services/ApiManager/ApiManager";
import AddMakeModelYearDialog from "./AddMakeModelYearDialog";
import { normalizeOemMmyData } from "./mmyUtils";
import { getConfig } from "../../../../config";

// Helper function to get filtered domains matching search text
const getFilteredDomains = (domains, searchText) => {
  return domains?.length > 0
    ? domains.filter((domain) =>
        domain.toLowerCase().includes(searchText.toLowerCase())
      )
    : [];
};

// Helper function to check if all filtered items are selected
const areAllItemsSelected = (filteredItems, selectedItems) => {
  return (
    filteredItems.length > 0 &&
    filteredItems.every((item) => selectedItems.includes(item))
  );
};

import {
  Backdrop,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

// Custom Delete Icon SVG Component
const CustomDeleteIcon = ({ disabled }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.55534 16C3.06645 16 2.64793 15.8259 2.29978 15.4778C1.95163 15.1296 1.77756 14.7111 1.77756 14.2222V2.66667H0.888672V0.888889H5.33312V0H10.6664V0.888889H15.1109V2.66667H14.222V14.2222C14.222 14.7111 14.0479 15.1296 13.6998 15.4778C13.3516 15.8259 12.9331 16 12.4442 16H3.55534ZM12.4442 2.66667H3.55534V14.2222H12.4442V2.66667ZM5.33312 12.4444H7.11089V4.44444H5.33312V12.4444ZM8.88867 12.4444H10.6664V4.44444H8.88867V12.4444Z"
      fill={disabled ? "#b8b9b9" : "#121415"}
      fillOpacity={disabled ? "1" : "0.6"}
    />
  </svg>
);

CustomDeleteIcon.propTypes = {
  disabled: PropTypes.bool,
};

const MakeModelYear = () => {
  const readOnly_OEMAdmin =
    sessionStorage.getItem("readonly_OEMAdmin") === "true";

  // Mock data states
  const [makeMyModel, setMakeMyModel] = useState([]);
  const [oemMmyOptions, setOemMmyOptions] = useState([]);
  const [filteredMakeMyModel, setFilteredMakeMyModel] = useState([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [mmySortOrder, setMmySortOrder] = useState("asc");
  const [loader, setLoader] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState("");
  const [portfolioName, setPortfolioName] = useState("AUDI");

  // Filter states
  const [domainChecked, setDomainChecked] = useState([]);
  const [makeChecked, setMakeChecked] = useState([]);
  const [domainSearch, setDomainSearch] = useState("");
  const [makeSearch, setMakeSearch] = useState("");
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [makeDropdownOpen, setMakeDropdownOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // ECU Types state
  const [ecuTypes, setEcuTypes] = useState([]);

  // Modal states
  const [addMakeModelModalOpen, setAddMakeModelModalOpen] = useState(false);
  const [makeModelForm, setMakeModelForm] = useState({
    make: "",
    model: "",
    year: [],
    ecuType: "",
  });
  const [makeInputValue, setMakeInputValue] = useState("");
  const [modelInputValue, setModelInputValue] = useState("");
  const [makeError, setMakeError] = useState(false);
  const [modelError, setModelError] = useState(false);
  const [yearError, setYearError] = useState(false);
  const [ecuTypeError, setEcuTypeError] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalYears, setOriginalYears] = useState([]);
  const [showDeleteMMY, setShowDeleteMMY] = useState(false);
  const [showEcuTypeDropdown, setShowEcuTypeDropdown] = useState(true);

  useEffect(() => {
    const portfolioId =
      selectedPortfolio || sessionStorage.getItem("portfolioId");
    if (!portfolioId) return;

    let isMounted = true;
    const fetchOemMmyOptions = async () => {
      setLoader(true);
      try {
        const response = await APIManager.getOEMAdminContentsMMY(portfolioId);
        if (!isMounted) return;
        const normalized = normalizeOemMmyData(response);
        setOemMmyOptions(normalized);
      } catch {
        // fetch error handled
      } finally {
        if (isMounted) {
          setLoader(false);
        }
      }
    };

    fetchOemMmyOptions();
    return () => {
      isMounted = false;
    };
  }, [selectedPortfolio]);

  useEffect(() => {
    const portfolioId =
      selectedPortfolio || sessionStorage.getItem("portfolioId");
    if (!portfolioId) return;

    let isMounted = true;
    const fetchEcuTypes = async () => {
      try {
        const response = await APIManager.getOEMAdminEcuTypes();
        if (!isMounted) return;
        setEcuTypes(response || []);
      } catch {
        // fetch error handled
      }
    };

    fetchEcuTypes();
    return () => {
      isMounted = false;
    };
  }, [selectedPortfolio]);

  // Fetch ECU Type Mappings
  useEffect(() => {
    const portfolioId =
      selectedPortfolio || sessionStorage.getItem("portfolioId");
    if (!portfolioId) return;

    let isMounted = true;
    const fetchECUTypeMappings = async () => {
      setLoader(true);
      try {
        const response =
          await APIManager.getOEMAdminECUTypeMappings(portfolioId);
        if (!isMounted) return;

        // Transform the API response into table data
        const transformedData = transformECUMappingsToTableData(
          response,
          portfolioName,
        );
        setMakeMyModel(transformedData);
      } catch {
        // fetch error handled
      } finally {
        if (isMounted) {
          setLoader(false);
        }
      }
    };

    fetchECUTypeMappings();
    return () => {
      isMounted = false;
    };
  }, [selectedPortfolio, portfolioName]);

  // Transform ECU mappings API response to table data
  const transformECUMappingsToTableData = (apiData, portfolioName) => {
    if (!apiData || typeof apiData !== "object") return [];

    const groupedData = {};

    // Group by model name AND ecuType combination
    Object.entries(apiData).forEach(([key, ecuType]) => {
      // Parse key format: "Model_Year" e.g., "500_2020" or "HarmanModel1_2025"
      const parts = key.split("_");
      const year = parts.at(-1);
      const model = parts.slice(0, -1).join("_");

      // Create unique key for model + ecuType combination
      const groupKey = `${model}_${ecuType}`;

      if (!groupedData[groupKey]) {
        groupedData[groupKey] = {
          model,
          ecuType,
          years: [],
        };
      }
      groupedData[groupKey].years.push(year);
    });

    // Create table rows with parent-child structure
    const tableData = [];
    let rowId = 0;

    Object.values(groupedData).forEach((group) => {
      const parentId = `parent_${rowId}`;

      // Parent row: Portfolio Model
      tableData.push({
        id: parentId,
        name: `${portfolioName} ${group.model}`,
        domain: portfolioName,
        ecuTypes: [group.ecuType],
        isParent: true,
        hasChildren: true,
        years: group.years,
      });

      // Child rows: One for each year
      group.years.forEach((year) => {
        rowId++;
        tableData.push({
          id: `child_${rowId}`,
          parentId: parentId,
          name: `${portfolioName} ${group.model} ${year}`,
          domain: portfolioName,
          ecuTypes: [group.ecuType],
          isChild: true,
          year: year,
        });
      });

      rowId++;
    });

    return tableData;
  };

  // Sorting handler for Make Model Year table
  const handleMmySortToggle = () => {
    setMmySortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const getDisplayName = (item) => {
    const displayName = item.name.replace(portfolioName + " ", "");
    if (item.isChild) {
      const modelOnly = item.year
        ? displayName.replace(` ${item.year}`, "")
        : displayName;
      return `${modelOnly} ${item.year || ""}`.trim();
    }
    return `${displayName}`.trim();
  };

  // Sorted items based on sort order - keep parent-child relationships
  const sortedManufacturers = (() => {
    const sourceData = filteredMakeMyModel.length > 0 ? filteredMakeMyModel : makeMyModel;
    
    // Separate parent and child rows
    const parents = sourceData.filter(item => item.isParent);
    const childrenByParentId = {};
    
    sourceData.forEach(item => {
      if (item.isChild && item.parentId) {
        if (!childrenByParentId[item.parentId]) {
          childrenByParentId[item.parentId] = [];
        }
        childrenByParentId[item.parentId].push(item);
      }
    });
    
    // Sort only parent rows
    const sortedParents = [...parents].sort((a, b) => {
      const nameA = getDisplayName(a);
      const nameB = getDisplayName(b);
      if (mmySortOrder === "asc") {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
    
    // Reconstruct with parents and their children
    const result = [];
    sortedParents.forEach(parent => {
      result.push(parent);
      if (childrenByParentId[parent.id]) {
        result.push(...childrenByParentId[parent.id]);
      }
    });
    
    return result;
  })();

  // Helper function to get makes in a domain
  const getMakesInDomain = (domain) => {
    return makeMyModel
      .filter((item) => item.domain === domain)
      .map((item) => item.name.replace(portfolioName + " ", "").split(" ")[0]);
  };

  // Helper function to remove makes from a domain
  const removeMakesFromDomain = (domain) => {
    const makesInDomain = getMakesInDomain(domain);
    setMakeChecked((prevMakes) =>
      prevMakes.filter((make) => !makesInDomain.includes(make))
    );
  };

  // Handler for Domain checkbox toggle
  const handleDomainToggle = (domain) => {
    setDomainChecked((prev) => {
      const isRemoving = prev.includes(domain);

      if (isRemoving) {
        removeMakesFromDomain(domain);
        return prev.filter((item) => item !== domain);
      } else {
        return [...prev, domain];
      }
    });
  };

  // Handler for Make checkbox toggle
  const handleMakeToggle = (make) => {
    setMakeChecked((prev) => {
      if (prev.includes(make)) {
        return prev.filter((item) => item !== make);
      }
      return [...prev, make];
    });
  };

  // Handler for Reset to Default button
  const handleResetFilter = () => {
    setDomainChecked([]);
    setMakeChecked([]);
    setDomainSearch("");
    setMakeSearch("");
    setFilteredMakeMyModel([]);
  };

  // Handler for Cancel button
  const handleCancelFilter = () => {
    setFilterDrawerOpen(false);
  };

  // Handler for Apply button
  const handleApplyFilter = () => {
    const filtered = applyFilters(makeMyModel);
    setFilteredMakeMyModel(filtered);
    setFilterDrawerOpen(false);
  };

  const applyFilters = (data) => {
    let filtered = data;

    // Filter by Domain
    if (domainChecked.length > 0) {
      filtered = filtered.filter((item) => domainChecked.includes(item.domain));
    }

    // Filter by Make
    if (makeChecked.length > 0) {
      filtered = filtered.filter((item) => {
        const itemName = item.name.replace(portfolioName + " ", "").split(" ")[0];
        return makeChecked.includes(itemName);
      });
    }

    return filtered;
  };

  // Helper function to get available Makes based on selected Domains
  const getAvailableMakes = () => {
    if (domainChecked.length === 0) {
      // If no domain selected, show all makes
      return [...new Set(makeMyModel.map((item) => {
        const name = item.name.replace(portfolioName + " ", "").split(" ")[0];
            return name;
      }))];
    }
    // Filter makes by selected domains
    return [
      ...new Set(
        makeMyModel
          .filter((item) => domainChecked.includes(item.domain))
          .map((item) => item.name.replace(portfolioName + " ", "").split(" ")[0])
      ),
    ];
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const resetAddMMYForm = () => {
    setMakeModelForm({
      make: "",
      model: "",
      year: [],
      ecuType: "",
    });
    setMakeInputValue("");
    setModelInputValue("");
    setMakeError(false);
    setModelError(false);
    setYearError(false);
    setEcuTypeError(false);
    setShowEcuTypeDropdown(true);
    setOriginalYears([]);
  };

  const handleAddMMY = () => {
    setIsEditMode(false);
    resetAddMMYForm();
    setAddMakeModelModalOpen(true);
  };

  const handleEditMMY = (item) => {
    setIsEditMode(true);

    // Extract model name from the format "AUDI ModelName"
    const modelName = item.name.replace(portfolioName + " ", "").trim();
    const yearsArray = item?.years || [];

    // Track original years to compare later
    setOriginalYears([...yearsArray]);

    setMakeModelForm({
      make: portfolioName,
      model: modelName,
      year: yearsArray,
      ecuType: item?.ecuTypes?.[0] || "",
    });
    setMakeInputValue(portfolioName);
    setModelInputValue(modelName);
    setShowEcuTypeDropdown(true);
    setAddMakeModelModalOpen(true);
  };

  const validateMMYForm = () => {
    const isEcuTypeValid = makeModelForm.ecuType.trim() !== "";
    const isMakeValid = makeModelForm.make.trim() !== "";
    const isModelValid = makeModelForm.model.trim() !== "";
    const isYearValid = makeModelForm.year.length > 0;

    setEcuTypeError(!isEcuTypeValid);
    setMakeError(!isMakeValid);
    setModelError(!isModelValid);
    setYearError(!isYearValid);

    return isEcuTypeValid && isMakeValid && isModelValid && isYearValid;
  };

  const saveMMYMappings = async (portfolioId) => {
    if (isEditMode) {
      const yearsToDelete = originalYears.filter(
        (year) => !makeModelForm.year.includes(year)
      );
      const yearsToAdd = makeModelForm.year.filter(
        (year) => !originalYears.includes(year)
      );

      for (const year of yearsToDelete) {
        const key = `${makeModelForm.model}_${year}`;
        await APIManager.deleteECUTYPEMapping(portfolioId, key);
      }

      for (const year of yearsToAdd) {
        const key = `${makeModelForm.model}_${year}`;
        const data = JSON.stringify({
          propertyKey: key,
          propertyValue: makeModelForm.ecuType,
        });
        await APIManager.putECUTYPEMapping(portfolioId, data);
      }
    } else {
      for (const year of makeModelForm.year) {
        const key = `${makeModelForm.model}_${year}`;
        const data = JSON.stringify({
          propertyKey: key,
          propertyValue: makeModelForm.ecuType,
        });
        await APIManager.putECUTYPEMapping(portfolioId, data);
      }
    }
  };

  const refreshMMYData = async (portfolioId) => {
    const response = await APIManager.getOEMAdminECUTypeMappings(portfolioId);
    const transformedData = transformECUMappingsToTableData(response, portfolioName);
    setMakeMyModel(transformedData);

    const isFilterActive = domainChecked.length > 0 || makeChecked.length > 0;
    setFilteredMakeMyModel(isFilterActive ? applyFilters(transformedData) : []);
  };

  const handleSaveMMY = async () => {
    if (!validateMMYForm()) return;

    setLoader(true);
    try {
      const portfolioId = selectedPortfolio || sessionStorage.getItem("portfolioId");
      await saveMMYMappings(portfolioId);
      await refreshMMYData(portfolioId);

      setAddMakeModelModalOpen(false);
      resetAddMMYForm();
      setOriginalYears([]);
      showSnackbar("Make Model Year saved successfully", "success");
    } catch {
      showSnackbar("Failed to save Make Model Year. Please try again.", "error");
    } finally {
      setLoader(false);
    }
  };

  const handleCloseAddMMY = () => {
    setAddMakeModelModalOpen(false);
    resetAddMMYForm();
  };

  const handleDeleteMMY = () => {
    setShowDeleteMMY(true);
  };

  const confirmDeleteMMY = () => {
    setShowDeleteMMY(false);
  };

  const getDataToSend = (portfolio) => {
    setSelectedPortfolio(portfolio);
    setPortfolioName(portfolio || "AUDI");
  };

  const testMMYItem = {
    name: `${portfolioName} TestModel`,
    years: ["2020"],
    ecuTypes: ["ECU Type"],
    domain: portfolioName,
  };

  // Get unique domains from makeMyModel
  const uniqueDomains = [...new Set(makeMyModel.map((item) => item.domain))];

  const ecuTypeOptions = ecuTypes || [];

  const selectedMakeGroup = oemMmyOptions.find(
    (group) => group.manufacturerName === makeModelForm.make,
  );
  const availableModelOptions = selectedMakeGroup
    ? selectedMakeGroup.models
        .map((model) => model.modelName)
        .filter((value, index, self) => self.indexOf(value) === index)
    : [];
  const selectedModelEntry = selectedMakeGroup?.models?.find(
    (model) => model.modelName === makeModelForm.model,
  );
  const availableYearOptions = selectedModelEntry?.years || [];

  return (
    <>
      <Topheader />
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Box
        sx={{
          marginTop: "3%",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "-webkit-box",
          }}
        >
          {getConfig().REACT_APP_PORTAL === "oem-admin" && (
            <Sidebar sendDataToParent={getDataToSend} page="make-model-year" />
          )}
          <Box sx={{ flexGrow: 1, marginTop: "16px", marginLeft: "20px" }}>
            <Box
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              width="89%"
            >
              <Typography sx={{ fontSize: "20px", marginTop: "2%" }}>
                {portfolioName} - Portfolio Settings
              </Typography>
            </Box>
            <Divider sx={{ marginTop: "2% !important", width: "89%" }} />

            <Box
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              width="89%"
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "2%",
                }}
              >
                <Typography sx={{ fontSize: "20px" }}>
                  Make Model Year
                </Typography>
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <IconButton
                    onClick={() => setFilterDrawerOpen(true)}
                    sx={{
                      border: "1px solid #E0E0E0",
                      borderRadius: "4px",
                      padding: "6px",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    <FilterAltOutlinedIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                  <Button
                    onClick={handleAddMMY}
                    variant="contained"
                    sx={{
                      height: "32px",
                      padding: "6px 16px",
                      textTransform: "none",
                      borderRadius: "4px",
                      backgroundColor: "#0476E2",
                      fontSize: "13px",
                      fontWeight: 400,
                      whiteSpace: "nowrap",
                    }}
                    disabled={readOnly_OEMAdmin}
                  >
                    Map Make Model Year
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Main Table */}
            <TableContainer
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "4px",
                marginBottom: "16px",
                backgroundColor: "#FFFFFF",
                width: "89%",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F5F5F5" }}>
                    <TableCell
                      sx={{
                        width: "30%",
                        cursor: "pointer",
                        userSelect: "none",
                        padding: "8px 16px",
                        border: "none",
                      }}
                      onClick={handleMmySortToggle}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: "#121415",
                          }}
                        >
                          Name
                        </Typography>
                        {mmySortOrder === "asc" ? (
                          <ArrowDownwardIcon
                            sx={{ fontSize: "18px", color: "#12141599" }}
                          />
                        ) : (
                          <ArrowUpwardIcon
                            sx={{ fontSize: "18px", color: "#12141599" }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ width: "40%", padding: "8px 16px", border: "none" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#121415",
                        }}
                      >
                        Mapped ECU Types
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedManufacturers.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        sx={{ textAlign: "center", padding: "32px 16px" }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#717273",
                            fontStyle: "italic",
                          }}
                        >
                          No records available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedManufacturers.map((item) => (
                      <TableRow
                        key={item.id}
                        sx={{
                          height: "40px",
                          "&:hover": {
                            backgroundColor: "#FAFAFA",
                          },
                        }}
                      >
                        <TableCell sx={{ padding: "8px 16px" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#121415",
                                fontWeight: 600,
                              }}
                            >
                              {(() => {
                                const ecuType = item.ecuTypes?.[0] || "";
                                const displayName = item.name.replace(
                                  portfolioName + " ",
                                  "",
                                );
                                if (item.isChild) {
                                  const modelOnly = item.year
                                    ? displayName.replace(` ${item.year}`, "")
                                    : displayName;
                                  return `${modelOnly} ${ecuType} ${item.year || ""}`.trim();
                                }
                                return `${displayName} ${ecuType}`.trim();
                              })()}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell sx={{ padding: "8px 16px" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {item.ecuTypes.map((ecuType) => (
                                <Typography
                                  key={ecuType}
                                  sx={{
                                    fontSize: "14px",
                                    color: "#5a5b5c",
                                  }}
                                >
                                  {ecuType}
                                </Typography>
                              ))}
                            </Box>
                            {item.isParent && (
                              <IconButton
                                size="small"
                                onClick={() => handleEditMMY(item)}
                                sx={{
                                  padding: "4px",
                                  color: "#717273",
                                }}
                                disabled={readOnly_OEMAdmin}
                              >
                                <ModeEditOutlineIcon sx={{ fontSize: "18px" }} />
                              </IconButton>
                            )}
                            {item.isChild && (
                              <IconButton
                                size="small"
                                sx={{
                                  padding: "4px",
                                  color: "#717273",
                                }}
                                disabled
                              >
                                <CustomDeleteIcon disabled />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>

      {/* Filter Drawer */}
      {filterDrawerOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: 340,
            bgcolor: "background.paper",
            boxShadow: 3,
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Sticky Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              background: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: "1px solid #eee",
              zIndex: 2,
            }}
          >
            <Typography variant="h6">Filter</Typography>
            <IconButton
              onClick={() => {
                setFilterDrawerOpen(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {/* Scrollable Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Make Dropdown with icon and toggle */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  mb: 1,
                }}
                onClick={() =>
                  setDomainDropdownOpen((open) => !open)
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ArrowDropDownIcon
                    sx={{
                      mr: 1,
                      transform: domainDropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                  <Typography sx={{ fontWeight: 500 }}>
                    Make
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "14px", color: "#666" }}
                >
                  {uniqueDomains?.length > 0
                    ? uniqueDomains.length
                    : 0}
                </Typography>
              </Box>
              {domainDropdownOpen && (
                <Box
                  sx={{
                    bgcolor: "#fff",
                    ml: 3,
                  }}
                >
                  {/* Search Bar and Select All */}
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      bgcolor: "#1214150D",
                      borderRadius: "4px",
                    }}
                  >
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        const allDomains = getFilteredDomains(
                          uniqueDomains,
                          domainSearch
                        );
                        const allSelected = areAllItemsSelected(
                          allDomains,
                          domainChecked
                        );
                        if (allSelected) {
                          setDomainChecked([]);
                        } else {
                          setDomainChecked(allDomains);
                        }
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: "13px",
                        color: "#0476E2",
                        minWidth: "auto",
                        padding: 0,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {(() => {
                        const allDomains = getFilteredDomains(
                          uniqueDomains,
                          domainSearch
                        );
                        const allSelected = areAllItemsSelected(
                          allDomains,
                          domainChecked
                        );
                        return allSelected
                          ? "Unselect All"
                          : "Select All";
                      })()}
                    </Button>
                    <TextField
                      size="small"
                      placeholder="Search for Domain"
                      value={domainSearch}
                      onChange={(e) =>
                        setDomainSearch(e.target.value)
                      }
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          height: "32px",
                          fontSize: "13px",
                          backgroundColor: "#fff",
                          borderRadius: "4px",
                        },
                      }}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{
                                  fontSize: 18,
                                  color: "#999",
                                }}
                              />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                  {/* Scrollable Options */}
                  <Box
                    sx={{
                      maxHeight: 120,
                      overflowY: "auto",
                    }}
                  >
                    {uniqueDomains?.length > 0
                      ? uniqueDomains
                          .filter((domain) =>
                            domain
                              .toLowerCase()
                            .includes(
                              domainSearch.toLowerCase()
                            )
                          )
                          .map((domain) => (
                            <Box
                              key={domain}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                py: 0.5,
                                px: 1,
                                cursor: "pointer",
                              bgcolor:
                                domainChecked.includes(
                                  domain
                                )
                                  ? "#E5F3FF"
                                  : "transparent",
                                "&:hover": {
                                bgcolor:
                                  domainChecked.includes(
                                    domain
                                  )
                                    ? "#E5F3FF"
                                    : "#f5f5f5",
                                },
                              }}
                            onClick={() =>
                              handleDomainToggle(domain)
                            }
                            >
                              <Checkbox
                                size="small"
                              checked={domainChecked.includes(
                                domain
                              )}
                                sx={{
                                  padding: "4px",
                                  "& .MuiSvgIcon-root": {
                                    fontSize: 18,
                                  },
                                color:
                                  domainChecked.includes(
                                    domain
                                  )
                                    ? "#0476E2"
                                    : undefined,
                                  "&.Mui-checked": {
                                    color: "#0476E2",
                                  },
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                color:
                                  domainChecked.includes(
                                    domain
                                  )
                                    ? "#0476E2"
                                    : "#121415",
                                fontWeight:
                                  domainChecked.includes(
                                    domain
                                  )
                                    ? 500
                                    : 400,
                                }}
                              >
                                {domain}
                              </Typography>
                            </Box>
                          ))
                      : []}
                  </Box>
                </Box>
              )}
            </Box>
            {/* Model Dropdown with icon and toggle */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor:
                    domainChecked.length > 0
                      ? "pointer"
                      : "not-allowed",
                  mb: 1,
                  opacity: domainChecked.length > 0 ? 1 : 0.5,
                }}
                onClick={() => {
                  if (domainChecked.length > 0) {
                    setMakeDropdownOpen((open) => !open);
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ArrowDropDownIcon
                    sx={{
                      mr: 1,
                      transform: makeDropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                  <Typography sx={{ fontWeight: 500 }}>
                    Model
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "14px", color: "#666" }}
                >
                  {getAvailableMakes().length}
                </Typography>
              </Box>
              {makeDropdownOpen &&
                domainChecked.length > 0 && (
                <Box
                  sx={{
                    bgcolor: "#fff",
                    ml: 3,
                  }}
                >
                  {/* Search Bar and Select All */}
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      bgcolor: "#1214150D",
                      borderRadius: "4px",
                    }}
                  >
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                          const allMakes =
                            getAvailableMakes().filter(
                              (make) =>
                          make
                            ?.toLowerCase()
                                  .includes(
                                    makeSearch.toLowerCase()
                                  )
                        );
                        const allSelected =
                          allMakes.length > 0 &&
                            allMakes.every((make) =>
                              makeChecked.includes(make)
                            );
                        if (allSelected) {
                          setMakeChecked([]);
                        } else {
                          setMakeChecked(allMakes);
                        }
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: "13px",
                        color: "#0476E2",
                        minWidth: "auto",
                        padding: 0,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {(() => {
                          const allMakes =
                            getAvailableMakes().filter(
                              (make) =>
                          make
                            ?.toLowerCase()
                                  .includes(
                                    makeSearch.toLowerCase()
                                  )
                        );
                        const allSelected =
                          allMakes.length > 0 &&
                            allMakes.every((make) =>
                              makeChecked.includes(make)
                            );
                          return allSelected
                            ? "Unselect All"
                            : "Select All";
                      })()}
                    </Button>
                    <TextField
                      size="small"
                      placeholder="Search for Model"
                      value={makeSearch}
                        onChange={(e) =>
                          setMakeSearch(e.target.value)
                        }
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          height: "32px",
                          fontSize: "13px",
                          backgroundColor: "#fff",
                          borderRadius: "4px",
                        },
                      }}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{
                                  fontSize: 18,
                                  color: "#999",
                                }}
                              />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                  {/* Scrollable Options */}
                  <Box
                    sx={{
                      maxHeight: 120,
                      overflowY: "auto",
                    }}
                  >
                    {getAvailableMakes()
                      .filter((make) =>
                          make
                            ?.toLowerCase()
                            .includes(
                              makeSearch.toLowerCase()
                            )
                      )
                      .map((make) => {
                          const isChecked =
                            makeChecked.includes(make);
                        return (
                          <Box
                            key={make}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              py: 0.5,
                              px: 1,
                              cursor: "pointer",
                                bgcolor: isChecked
                                  ? "#E5F3FF"
                                  : "transparent",
                              "&:hover": {
                                  bgcolor: isChecked
                                    ? "#E5F3FF"
                                    : "#f5f5f5",
                              },
                            }}
                              onClick={() =>
                                handleMakeToggle(make)
                              }
                          >
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              sx={{
                                padding: "4px",
                                "& .MuiSvgIcon-root": {
                                  fontSize: 18,
                                },
                                  color: isChecked
                                    ? "#0476E2"
                                    : undefined,
                                "&.Mui-checked": {
                                  color: "#0476E2",
                                },
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "13px",
                                  color: isChecked
                                    ? "#0476E2"
                                    : "#121415",
                                  fontWeight: isChecked
                                    ? 500
                                    : 400,
                              }}
                            >
                              {make}
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          {/* Sticky Footer */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              background: "#fff",
              borderTop: "1px solid #eee",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              zIndex: 2,
            }}
          >
            {/* Left: Reset to Default */}
            <Button
              variant="text"
              color="inherit"
              onClick={handleResetFilter}
              sx={{
                maxWidth: "125px",
                whiteSpace: "nowrap",
                fontWeight: 400,
                textTransform: "none",
              }}
            >
              Reset to Default
            </Button>
            {/* Right: Cancel and Apply */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancelFilter}
                sx={{
                  whiteSpace: "nowrap",
                  fontWeight: 400,
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyFilter}
                sx={{
                  whiteSpace: "nowrap",
                  fontWeight: 400,
                  textTransform: "none",
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <AddMakeModelYearDialog
        open={addMakeModelModalOpen}
        onCloseReset={handleCloseAddMMY}
        onCancel={() => setAddMakeModelModalOpen(false)}
        onSave={handleSaveMMY}
        isLoading={loader}
        isEditMode={isEditMode}
        oemMmyOptions={oemMmyOptions}
        makeModelForm={makeModelForm}
        setMakeModelForm={setMakeModelForm}
        makeInputValue={makeInputValue}
        setMakeInputValue={setMakeInputValue}
        modelInputValue={modelInputValue}
        setModelInputValue={setModelInputValue}
        makeError={makeError}
        setMakeError={setMakeError}
        modelError={modelError}
        setModelError={setModelError}
        yearError={yearError}
        setYearError={setYearError}
        ecuTypeError={ecuTypeError}
        setEcuTypeError={setEcuTypeError}
        showEcuTypeDropdown={showEcuTypeDropdown}
        setShowEcuTypeDropdown={setShowEcuTypeDropdown}
        ecuTypeOptions={ecuTypeOptions}
        availableModelOptions={availableModelOptions}
        availableYearOptions={availableYearOptions}
      />

      {process.env.NODE_ENV === "test" && (
        <Box sx={{ display: "none" }}>
          <Button
            data-testid="mmy-edit-test"
            onClick={() => handleEditMMY(testMMYItem)}
          >
            Test Edit
          </Button>
          <Button
            data-testid="mmy-delete-test"
            onClick={() => handleDeleteMMY()}
          >
            Test Delete
          </Button>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteMMY}
        onClose={() => setShowDeleteMMY(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Make Model Year</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this Make Model Year entry?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setShowDeleteMMY(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteMMY}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MakeModelYear;
